import { useState, useCallback, useEffect, useRef } from 'react';
import { useLazyGetPromoActionListQuery } from '../../../../../services/cellcast.v2';
import { PromoAction, GetListParams } from '../../../../../services/cellcast.v2.types';
import { PromoActionState, PromoActionHookReturn } from '../PromoAction.type';
import { useDebounce } from './useDebounce';
import { useSelector } from 'react-redux';
import { getAllUserDetails } from '../../accounts/Overview/OverviewSlice/UserDetailsSlice';

const DEFAULT_PAGE = 1;
const DEFAULT_LIMIT = 10;
const DEBOUNCE_DELAY = 600;
const LOADING_DELAY = 300;

export const usePromoActions = (): PromoActionHookReturn => {
  const [getPromoActionList, { data: promoActionResponse, isLoading, error }] = useLazyGetPromoActionListQuery();
  
  const userDetails = useSelector(getAllUserDetails)
  const userRole = userDetails?.role
  const isAdmin = ['superAdmin', 'accountManager', 'subAdmin', 'sales', 'affiliate'].includes(userRole)
  
  const [state, setState] = useState<PromoActionState>({
    page: DEFAULT_PAGE,
    limit: DEFAULT_LIMIT,
    searchTerm: '',
    sort: '',
    sortBy: '',
    sortOrder: '',
  });

  // Tracking refs
  const isInitialMount = useRef(true);
  const stateRef = useRef(state);
  const lastFetchParamsRef = useRef<{
    page: number;
    limit: number;
    search: string;
    sort?: string;
    sortBy?: string;
    sortOrder?: string;
  }>({
    page: DEFAULT_PAGE,
    limit: DEFAULT_LIMIT,
    search: '',
    sort: '',
    sortBy: '',
    sortOrder: '',
  });

  // Update stateRef when state changes
  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  const debouncedSearchTerm = useDebounce(state.searchTerm, DEBOUNCE_DELAY);

  // Check if fetch params have changed
  const shouldFetchData = useCallback((currentParams: {
    page: number;
    limit: number;
    search: string;
    sort?: string;
    sortBy?: string;
    sortOrder?: string;
  }) => {
    const lastParams = lastFetchParamsRef.current;
    
    return (
      currentParams.page !== lastParams.page ||
      currentParams.search !== lastParams.search ||
      currentParams.limit !== lastParams.limit ||
      currentParams.sort !== lastParams.sort ||
      currentParams.sortBy !== lastParams.sortBy ||
      currentParams.sortOrder !== lastParams.sortOrder
    );
  }, []);

  const fetchPromoActions = useCallback(async (params?: Partial<PromoActionState>) => {
    const currentState = stateRef.current;
    const pageToUse = params?.page ?? currentState.page;
    const searchToUse = params?.searchTerm ?? currentState.searchTerm;
    const limitToUse = params?.limit ?? currentState.limit;
    const sortToUse = params?.sort ?? currentState.sort;
    const sortByToUse = params?.sortBy ?? currentState.sortBy;
    const sortOrderToUse = params?.sortOrder ?? currentState.sortOrder;

    // Current fetch params
    const currentParams = {
      page: pageToUse,
      limit: limitToUse,
      search: searchToUse,
      sort: sortToUse,
      sortBy: sortByToUse,
      sortOrder: sortOrderToUse,
    };

    // Check if we need to fetch
    if (!params?.forceRefresh && !shouldFetchData(currentParams)) {
      return;
    }

    // Update last fetch params
    lastFetchParamsRef.current = { ...currentParams };

    if (params) {
      setState(prev => ({ ...prev, ...params }));
    }
    
    try {
      const queryParams: GetListParams & { 
        sort?: string;
        sortBy?: string;
        sortOrder?: string;
      } = {
        page: pageToUse,
        limit: limitToUse,
      };

      if (searchToUse && searchToUse.trim() !== '') {
        queryParams.search = searchToUse.trim();
      }

      // Handle both sort formats
      if (sortToUse && sortToUse.trim() !== '') {
        queryParams.sort = sortToUse.trim();
      }

      if (sortByToUse && sortByToUse.trim() !== '') {
        queryParams.sortBy = sortByToUse.trim();
      }

      if (sortOrderToUse && sortOrderToUse.trim() !== '') {
        queryParams.sortOrder = sortOrderToUse.trim();
      }

      await getPromoActionList(queryParams);
      
      if (!isInitialMount.current) {
        // Add small delay to show loading state only for subsequent fetches
        await new Promise(resolve => setTimeout(resolve, LOADING_DELAY));
      }
    } catch (error) {
      console.error('Error fetching promo actions:', error);
    }
  }, [getPromoActionList, shouldFetchData]); // Removed state dependency

  const handlePageChange = useCallback((newPage: number) => {
    fetchPromoActions({ 
      page: newPage,
      forceRefresh: true 
    });
  }, [fetchPromoActions]);

  const handleSearch = useCallback((term: string) => {
    setState(prev => ({ ...prev, searchTerm: term, page: DEFAULT_PAGE }));
  }, []);

  // Effect for handling debounced search
  useEffect(() => {
    if (isInitialMount.current) {
      return;
    }

    // Only fetch if the debounced search term is different from the last fetch
    if (debouncedSearchTerm !== lastFetchParamsRef.current.search) {
      fetchPromoActions({ 
        searchTerm: debouncedSearchTerm, 
        page: DEFAULT_PAGE
      });
    }
  }, [debouncedSearchTerm, fetchPromoActions]);

  // Initial fetch - only run once when component mounts
  useEffect(() => {
    isInitialMount.current = false;
    fetchPromoActions({ forceRefresh: true });
  }, []); // Empty dependency array

  return {
    promoData: promoActionResponse?.data?.items || null,
    total: promoActionResponse?.data?.total || 0,
    isLoading,
    error,
    searchTerm: state.searchTerm,
    fetchPromoActions,
    handlePageChange,
    handleSearch,
    isAdmin,
  };
}; 